import React, { useRef, useState } from 'react';

import styles from './index.module.css';

function ChevronLeftIcon() {
  return (
    <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
      <path d='M10 3L5 8L10 13' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
      <path d='M6 3L11 8L6 13' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
}

// `options` is an array of { kicker, name, icon, content }. On desktop both
// panels are shown side by side; on mobile they become a single swipeable track
// driven by the tab header and the prev/next controls.
export default function OptionTabs({ options }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const goTo = (index) => {
    const clamped = Math.max(0, Math.min(options.length - 1, index));
    const track = trackRef.current;
    if (track) {
      const panel = track.querySelectorAll(`.${styles.option_panel}`)[clamped];
      if (panel) {
        track.scrollTo({ left: panel.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }
    }
    setActive(clamped);
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index !== active) setActive(index);
  };

  return (
    <div className={styles.options}>
      <div className={styles.option_tabs} role='tablist'>
        {options.map((option, index) => (
          <button
            type='button'
            key={option.name}
            role='tab'
            aria-selected={active === index}
            className={`${styles.option_tab} ${active === index ? styles.option_tab_active : ''}`}
            onClick={() => goTo(index)}
          >
            <span className={styles.option_tab_icon}>{option.icon}</span>
            <span className={styles.option_tab_labels}>
              <span className={styles.option_tab_kicker}>{`Option ${index + 1} of ${options.length}`}</span>
              <span className={styles.option_tab_name}>{option.name}</span>
            </span>
          </button>
        ))}
      </div>

      <div className={styles.options_track} ref={trackRef} onScroll={handleScroll}>
        {options.map((option, index) => (
          <React.Fragment key={option.name}>
            {index > 0 && <span className={styles.or_badge}>or</span>}
            <div className={styles.option_panel} role='tabpanel'>
              <div className={styles.option_head}>
                <span className={styles.option_head_icon}>{option.icon}</span>
                <span className={styles.option_head_text}>
                  <span className={styles.step_label}>{option.kicker}</span>
                  <span className={styles.step_title}>{option.name}</span>
                </span>
              </div>
              {option.content}
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className={styles.option_nav}>
        <button
          type='button'
          className={styles.option_nav_btn}
          onClick={() => goTo(active - 1)}
          disabled={active === 0}
          aria-label='Previous option'
        >
          <ChevronLeftIcon />
        </button>
        <span className={styles.option_pager}>{`${active + 1} of ${options.length}`}</span>
        <button
          type='button'
          className={styles.option_nav_btn}
          onClick={() => goTo(active + 1)}
          disabled={active === options.length - 1}
          aria-label='Next option'
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
}
